import React, { useState } from 'react';
import { Form, Divider, message, Checkbox, Input } from 'antd';
import { ReactComponent as LinkSvg } from '@assets/icons/link.svg';
import { ReactComponent as CopySvg } from '@assets/icons/copy.svg';
import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import InviteMembersSelect from '@components/InviteMembersSelect/InviteMembersSelect';
import { nonNull } from '@helpers/non-null';
import { apiClient } from '@api/client';
import { useCurrentWorkspace } from '@hooks/workspace';
import { FormInstance } from 'antd/es/form/Form';
import type { FormValues } from '@components/Modals/ShareAssetModal/ShareAssetModal';

interface ISharingProps {
  link: string | null;
  previewLink: string | null;
  campaignId: string | null;
  form: FormInstance<FormValues>;
}

function Sharing({ link, previewLink, campaignId, form }: ISharingProps) {
  const [workspace] = useCurrentWorkspace();
  const [isLinkCopied, setIsLinkCopied] = useState<boolean>(false);
  const isShowMessageWatch = Form.useWatch('showMessage', form);

  const hasLink = !!link;

  const onCopyLink = () => {
    if (!hasLink) return;
    navigator.clipboard.writeText(link);
    setIsLinkCopied(true);
    message.success({
      content: 'Copied link to clipboard',
      className: 'message-dark-modal',
      icon: <SuccessSvg className="anticon" />
    });
  };

  return (
    <div className="share_asset_modal_form">
      <Form.Item
        className="share_asset_modal_users-item"
        label="Name"
        name="name"
      >
        <Input placeholder="Name" />
      </Form.Item>
      {hasLink && (
        <>
          <p className="modal_container__copy">Link URL</p>
          <div className="modal_container__link_block" onClick={onCopyLink}>
            <div className="link_row">
              <LinkSvg />
              <span className="link">{link}</span>
            </div>
            <CopySvg className={isLinkCopied ? 'isLinkCopied' : ''} />
          </div>
          {previewLink && (
            <a
              target="_blank"
              href={previewLink}
              className="share_asset_modal_preview-link"
              rel="noreferrer"
            >
              Preview link in new tab
            </a>
          )}
        </>
      )}
      <Divider className="modal_container_divider" />
      <Form.Item
        className="share_asset_modal_users-item"
        label="Share with people"
        name="userList"
        style={{ marginBottom: 16 }}
      >
        <InviteMembersSelect
          key={campaignId}
          queryEnabled={!!campaignId}
          placeholder="Enter name or email"
          membersQueryKey={
            workspace.permissions.listMembers
              ? ['workspace', 'members', 'list', workspace.id]
              : ['campaign', 'members', 'list', campaignId]
          }
          membersQueryFn={async ({ pageParam, searchQuery, signal }) => {
            if (workspace.permissions.listMembers) {
              const { data } =
                await apiClient.workspace.workspaceControllerListMembers(
                  {
                    workspaceId: workspace.id,
                    searchQuery,
                    after: pageParam
                  },
                  { signal }
                );
              return data;
            }
            const { data } =
              await apiClient.campaign.campaignControllerListMembers(
                {
                  campaignId: nonNull(campaignId),
                  searchQuery,
                  after: pageParam
                },
                { signal }
              );
            return data;
          }}
          groupsQueryKey={
            workspace.permissions.listMembers
              ? ['workspace', 'groups', 'list', workspace.id]
              : ['campaign', 'groups', 'list', campaignId]
          }
          groupsQueryFn={async ({ pageParam, searchQuery, signal }) => {
            if (workspace.permissions.listMembers) {
              const { data } =
                await apiClient.workspace.workspaceControllerListMemberGroups(
                  {
                    workspaceId: workspace.id,
                    after: pageParam,
                    searchQuery
                  },
                  { signal }
                );
              return data;
            }
            const { data } =
              await apiClient.campaign.campaignControllerListMemberGroups(
                {
                  campaignId: nonNull(campaignId),
                  after: pageParam,
                  searchQuery
                },
                { signal }
              );
            return data;
          }}
        />
      </Form.Item>
      {isShowMessageWatch && (
        <Form.Item
          className="share_asset_modal_users-item"
          name="customMessage"
          rules={[{ required: true, message: 'Message required.' }]}
          style={{ marginBottom: 16 }}
        >
          <Input.TextArea placeholder="Add custom message" />
        </Form.Item>
      )}
      <Form.Item
        className="share_asset_modal_checkbox"
        valuePropName="checked"
        name="showMessage"
      >
        <Checkbox className="b-checkbox-blue">Add a message</Checkbox>
      </Form.Item>
    </div>
  );
}

export default Sharing;
